import { type ResultSuppliers } from '../product-router';
import {
	type DecoratorResultSetStateProps,
	useDecorateWithResultAndAPISetState,
} from './use-decorate-with-result-and-api-set-state';
import { ExperiencePerformanceTypes, ExperienceTypes, UFOExperience } from '@atlassian/ufo';
import { useMemo, useRef } from 'react';
import { useDecorateWithTimings } from './use-decorate-post-query-with-timings';
import { COMPONENT } from '../../common/ufo-experiences';

/**
 * This decorator adds capabilities of API state tracking and debouncing to the plain PostQuerySupplier.
 * Think of this as an internal private method of useProductStateMachine.
 * @returns A post query supplier which also aware of setting its respective API state.
 */
export const usePostQueryDecorator = ({
	setAPIState,
	setState,
	postQueryItemSupplier,
	id,
}: Pick<ResultSuppliers, 'postQueryItemSupplier'> &
	Pick<DecoratorResultSetStateProps<{}>, 'setState' | 'setAPIState'> & {
		id: string;
	}) => {
	const ufoPostQueryLoadExperience = useRef<UFOExperience>(
		new UFOExperience(`${id}-post-query-results-load`, {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
			platform: { component: COMPONENT },
		}),
	);

	const decorateWithTimings = useDecorateWithTimings();
	const timedItemSupplier = useMemo(
		() => decorateWithTimings(postQueryItemSupplier),
		[postQueryItemSupplier, decorateWithTimings],
	);

	const decoratedPostQuery = useDecorateWithResultAndAPISetState({
		setState,
		setAPIState,
		ufoExperience: ufoPostQueryLoadExperience.current,
		searchItemSupplier: timedItemSupplier,
	});

	return decoratedPostQuery;
};
